import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import mapValues from 'lodash/mapValues';
import defaults from 'lodash/defaults';

import Config from '../../config';

import ModalNames from './modalNames';
import CustomReportsMenu from './customReportsMenu.component';
import { hideDecimalsSelector, negativeMinusSelector, displayZeroSelector } from '../../userData/store/selectors';

import { withPermissions, permissions } from '../../shared/authorization';
import { modalDataSelector } from '../../shared/modals/modals.selectors';

import {
  clientNameSelector,
  environmentSelector,
  environmentSuffixSelector,
  istClientIdSelector,
} from '../../shared/store/selectors';

import CustomReportsDataGrid from '../../shared/customReports/customReportsDataGrid.component';
import CustomReportsSetDataGrid from '../../shared/customReports/customReportsSetDataGrid.component';
import { ReduxAlert } from '../../shared/alert.component';
import JimJobInfoComponent from '../../shared/jim/jimJobInfo.component';

import {
  customReportPropTypes,
  customReportDataPropTypes,
} from '../../shared/customReports/propTypes';
import { OptionsPropType } from '../../shared/propTypes/common';
import useGridApi from '../../shared/hooks/useGridApi.hook';

import {
  customReportsDropdownSelector,
  customReportSelector,
  customReportParamsSelector,
  isCustomReportRunInProgressSelector,
  customReportResponseDataSelector,
  isReportSetOpenSelector,
  customReportSetReportsSelector,
  isBusySelector,
  customReportQueryParamsSelector,
  isFetchingCustomReportParamsSelector,
} from './store/selectors';

import {
  fetchCustomReports,
  selectCustomReport,
  fetchCustomReportParams,
  runCustomReport,
  updateCustomReportQueryParams,
  resetCustomReport,
  showResetCustomReportModal,
  hideResetCustomReportModal,
  verifyParamOptionsQuery,
} from './store/actions';
import { cachedGetUniqueQueryParams } from '../../shared/customReports/helpers';
import useDebounce from '../../menu/helpContent/hooks/useDebounce';
import { generateExportFileName } from '@common/utils/generateExportFileName';
import { useCustomReportsJimExport } from './useCustomReportsJimExport.hook';


const CustomReportsPage = ({
  customReportsDropdown,
  customReport,
  customReportsParamsOptions,
  runCustomReport,
  customReportData,
  isCustomReportRunInProgress,
  isReportSetOpen,
  selectCustomReport,
  customReportSetReports,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
  environmentSuffix,
  isBusy,
  updateCustomReportQueryParams,
  customReportQueryParams,
  fetchCustomReports,
  fetchCustomReportParams,
  resetCustomReport,
  environment,
  clientName,
  isFetchingCustomReportParams,
  istClientId,
  showResetCustomReportModal,
  hideResetCustomReportModal,
  resetCustomReportModalData,
  verifyParamOptionsQuery,
}) => {
  const { gridApi, onGridReady: onGridReadyForExcelExport } = useGridApi();
  useEffect(() => {
    fetchCustomReports();
  }, [
    fetchCustomReports,
  ]);

  const refetchCustomReportParams = useCallback((paramValues = {}) => {
    if (isReportSetOpen && customReportSetReports.length) {
      const { uniqueParams, paramToUniqueIdMapping } = cachedGetUniqueQueryParams(customReportSetReports);
      fetchCustomReportParams({
        reportSetId: customReport.reportSetId,
        paramToUniqueIdMapping,
        paramValues: uniqueParams.reduce((acc, { name, id }) => {
          acc[name] = paramValues[id] || '';
          return acc;
        }, {}),
      });
    }
    if (!isReportSetOpen && customReport) {
      fetchCustomReportParams({
        reportSetId: customReport.reportSetId,
        paramValues: defaults(
          { ...paramValues },
          mapValues(customReport.params, () => '')
        ),
      });
    }
  }, [isReportSetOpen, customReport, customReportSetReports, fetchCustomReportParams]);

  const debouncedRefetchCustomReportParams = useDebounce(refetchCustomReportParams, 1000);

  const updateCustomReportQueryParamsHandler = useCallback(params => {
    updateCustomReportQueryParams(params);
    debouncedRefetchCustomReportParams(params);
  }, [debouncedRefetchCustomReportParams, updateCustomReportQueryParams]);

  const generateCustomReportExportName = useCallback((reportSetName, customReportParamValues) =>
    generateExportFileName({
      reportName: reportSetName,
      paramValues: customReportParamValues,
      environmentSuffix,
    }), [environmentSuffix]);

  const { exportWithJim, isExportRunning: isJimExportRunning } = useCustomReportsJimExport({
    customReport,
    customReportSetReports,
    generateExportFileName: generateCustomReportExportName,
  });

  return (
    <div className="custom-reports-management-page">
      <CustomReportsMenu
        resetCustomReport={resetCustomReport}
        customReportsDropdown={customReportsDropdown}
        customReport={customReport}
        runCustomReport={runCustomReport}
        exportCustomReport={exportWithJim}
        selectCustomReport={selectCustomReport}
        customReportsParamsOptions={customReportsParamsOptions}
        isCustomReportRunInProgress={isCustomReportRunInProgress}
        isReportSetOpen={isReportSetOpen}
        disabledDownload={!customReportData}
        customReportData={customReportData}
        updateCustomReportQueryParams={updateCustomReportQueryParamsHandler}
        customReportQueryParams={customReportQueryParams}
        customReportSetReports={customReportSetReports}
        shouldDisplayZero={shouldDisplayZero}
        hideDecimals={hideDecimals}
        negativeMinus={negativeMinus}
        environmentSuffix={environmentSuffix}
        isBusy={isBusy}
        isExportInProgress={isJimExportRunning}
        environment={environment}
        clientName={clientName}
        gridApi={gridApi}
        generateCustomReportExportName={generateCustomReportExportName}
        refetchCustomReportParams={refetchCustomReportParams}
        isFetchingCustomReportParams={isFetchingCustomReportParams}
        istClientId={istClientId}
        showResetCustomReportModal={showResetCustomReportModal}
        hideResetCustomReportModal={hideResetCustomReportModal}
        resetCustomReportModalData={resetCustomReportModalData}
        verifyParamOptionsQuery={verifyParamOptionsQuery}
      />
      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters">
            <div className="col">
              <JimJobInfoComponent jobConfig={Config.jimJobs.customReportsExport} showProgressBar/>
              <ReduxAlert/>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>Custom Reports</h5>
              </div>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="col">
              {isReportSetOpen && customReportData
                ? <CustomReportsSetDataGrid
                  reports={customReportData.reports}
                  customReportSetReports={customReportSetReports}
                  reportSetId={customReport.reportSetId}
                  isGridLoading={isCustomReportRunInProgress || isBusy}
                  updatesParamsAndLocalStorage
                  queryParams={customReportQueryParams}
                  onGridReadyForExcelExport={onGridReadyForExcelExport}
                />
                : <CustomReportsDataGrid
                  columns={(customReportData && customReportData.columns) || []}
                  rows={(customReportData && customReportData.rows) || []}
                  types={(customReportData && customReportData.types) || {}}
                  isGridLoading={isCustomReportRunInProgress || isBusy}
                  serverSide={customReport && customReport.serverSide}
                  reportId={customReport && customReport.reportSetId}
                  queryParams={customReportQueryParams}
                  updatesParamsAndLocalStorage
                  onGridReadyForExcelExport={onGridReadyForExcelExport}
                />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CustomReportsPage.propTypes = {
  resetCustomReport: PropTypes.func.isRequired,
  selectCustomReport: PropTypes.func.isRequired,
  runCustomReport: PropTypes.func.isRequired,
  exportCustomReport: PropTypes.func,
  exportBatchReport: PropTypes.func,
  fetchCustomReports: PropTypes.func.isRequired,
  fetchCustomReportParams: PropTypes.func.isRequired,
  updateCustomReportQueryParams: PropTypes.func.isRequired,
  showResetCustomReportModal: PropTypes.func.isRequired,
  hideResetCustomReportModal: PropTypes.func.isRequired,

  isFetchingCustomReportParams: PropTypes.bool.isRequired,
  isCustomReportRunInProgress: PropTypes.bool.isRequired,
  isReportSetOpen: PropTypes.bool.isRequired,

  customReport: customReportPropTypes,
  customReportQueryParams: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])).isRequired,
  resetCustomReportModalData: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])).isRequired,

  customReportsDropdown: OptionsPropType,

  customReportsParamsOptions: PropTypes.objectOf(OptionsPropType),

  customReportData: customReportDataPropTypes,
  customReportSetReports: PropTypes.arrayOf(customReportPropTypes),

  shouldDisplayZero: PropTypes.bool.isRequired,
  hideDecimals: PropTypes.bool.isRequired,
  negativeMinus: PropTypes.bool.isRequired,
  environmentSuffix: PropTypes.string.isRequired,
  isBusy: PropTypes.bool,

  isExportInProgress: PropTypes.bool,
  exportCompletionPercent: PropTypes.number,
  environment: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  istClientId: PropTypes.string.isRequired,
  verifyParamOptionsQuery: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  customReport: customReportSelector(state),
  customReportData: customReportResponseDataSelector(state),
  customReportsParamsOptions: customReportParamsSelector(state),
  customReportsDropdown: customReportsDropdownSelector(state),
  isCustomReportRunInProgress: isCustomReportRunInProgressSelector(state),
  isReportSetOpen: isReportSetOpenSelector(state),
  customReportSetReports: customReportSetReportsSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
  environmentSuffix: environmentSuffixSelector(state),
  isBusy: isBusySelector(state),
  customReportQueryParams: customReportQueryParamsSelector(state),
  environment: environmentSelector(state),
  clientName: clientNameSelector(state),
  isFetchingCustomReportParams: isFetchingCustomReportParamsSelector(state),
  istClientId: istClientIdSelector(state),
  resetCustomReportModalData: modalDataSelector(state, ModalNames.ResetCustomReport),
});

export default withPermissions(permissions.CUSTOM_REPORTS)(connect(
  mapStateToProps,
  {
    fetchCustomReports,
    selectCustomReport,
    fetchCustomReportParams,
    runCustomReport,
    resetCustomReport,
    updateCustomReportQueryParams,
    showResetCustomReportModal,
    hideResetCustomReportModal,
    verifyParamOptionsQuery,
  }
)(CustomReportsPage));
